/**
 * Answer / AnswerHub — AEO ("Answer Engine Optimisation") content cluster.
 *
 *   /answers                       — hub listing all 78 (province × topic) pages
 *   /answers/:province/:topic      — single answer page
 *
 * Each answer page ships FOUR pieces of structured data that Google's 2025
 * AI Overview / Search Generative Experience explicitly looks for:
 *
 *   1. FAQPage         — appears in AI answers + classic FAQ rich snippets
 *   2. HowTo           — step-by-step recipe, surfaces in AI Overviews
 *   3. Speakable       — voice / Google Assistant / ChatGPT eligibility
 *   4. LocalBusiness   — province-scoped business entity (local AI queries)
 *
 * The frontend hydrates from a single GET /api/aeo/answers/:province/:topic
 * call (with a JS-rendered fallback so Googlebot sees content on first paint
 * if the API is slow). Owner: SEO / AEO. The pages are intentionally
 * dense, deeply linked, and Canadian-province-specific.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, BookOpen, Sparkles, CheckCircle2, HelpCircle, ChevronRight } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/* eslint-disable react/no-unescaped-entities */

// ───────────────────────── Schema helpers ─────────────────────────

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(sel.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}
function setJsonLd(id, payload) {
  let s = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!s) {
    s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-jsonld", id);
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(payload);
  return s;
}

/* ─────────────────────────────── Answer page ─────────────────────────────── */

// String labels per locale — Quebec is the only province with FR
// publication, so the dictionary is intentionally tight (no i18n
// framework needed).
const STR = {
  en: {
    breadcrumb_answers: "Answers",
    step_by_step: "Step-by-step in",
    why_this_matters: "Why this matters",
    faq: "Frequently asked",
    cta_heading: "Stop guessing — let Mark handle it",
    cta_body: (p) => `Central Rentals automates every step above for ${p.name} landlords. Generate the right notice, calculate the legal cap, file with ${p.lto_body}, and export to CRA — all in one place.`,
    cta_btn: "Choose a plan",
    cta_secondary: (p) => `See all ${p.name} features →`,
    more_answers: (p) => `More ${p.name} answers`,
    read_answer: "Read answer",
    how_to: "How to",
    what_is: "What is",
  },
  fr: {
    breadcrumb_answers: "Réponses",
    step_by_step: "Étape par étape au",
    why_this_matters: "Pourquoi c'est important",
    faq: "Questions fréquentes",
    cta_heading: "Arrêtez de deviner — laissez Mark s'en occuper",
    cta_body: (p) => `Central Rentals automatise chaque étape ci-dessus pour les propriétaires du ${p.name}. Générez le bon avis, calculez le plafond légal, déposez au ${p.lto_body} et exportez à l'ARC — tout au même endroit.`,
    cta_btn: "Commencer l'essai gratuit de 14 jours",
    cta_secondary: (p) => `Voir toutes les fonctions du ${p.name} →`,
    more_answers: (p) => `Plus de réponses pour le ${p.name}`,
    read_answer: "Lire la réponse",
    how_to: "Comment",
    what_is: "Qu'est-ce que",
  },
};

export default function Answer({ lang = "en" }) {
  const { province, topic } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  // FR is only published for Quebec. Redirect /fr/answers/<non-quebec>/*
  // to the EN canonical so we don't serve a French URL with English
  // content (hurts indexing + confuses users). Done in useEffect so we
  // don't violate the rules-of-hooks by early-returning before the data
  // hooks below.
  useEffect(() => {
    if (lang === "fr" && province && province !== "quebec") {
      navigate(`/answers/${province}/${topic}`, { replace: true });
    }
  }, [lang, province, topic, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = lang === "fr" ? "?lang=fr" : "";
        const r = await fetch(`${API}/api/aeo/answers/${province}/${topic}${qs}`);
        if (!r.ok) throw new Error("Not found");
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [province, topic, lang]);

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const titleText = `${data.question} — Central Rentals Canada`;
    document.title = titleText;
    const meta = [
      setMeta('meta[name="description"]', { name: "description", content: data.summary }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: data.canonical }),
      setMeta('meta[property="og:type"]', { property: "og:type", content: "article" }),
      setMeta('meta[property="og:title"]', { property: "og:title", content: titleText }),
      setMeta('meta[property="og:description"]', { property: "og:description", content: data.summary }),
      setMeta('meta[property="og:url"]', { property: "og:url", content: data.canonical }),
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
    ];
    // Update the root <html lang> attribute directly — setMeta() against
    // 'html[lang]' fails when the html element doesn't yet have a lang.
    const prevHtmlLang = document.documentElement.getAttribute("lang") || "";
    document.documentElement.setAttribute("lang", data.lang || "en");
    // hreflang link tags so Google knows the EN ↔ FR mirror pair (Quebec only)
    if (data.hreflang_fr) {
      meta.push(setMeta('link[rel="alternate"][hreflang="fr-CA"]', { rel: "alternate", hreflang: "fr-CA", href: data.hreflang_fr }));
      meta.push(setMeta('link[rel="alternate"][hreflang="x-default"]', { rel: "alternate", hreflang: "x-default", href: data.canonical }));
    }
    if (data.hreflang_en) {
      meta.push(setMeta('link[rel="alternate"][hreflang="en-CA"]', { rel: "alternate", hreflang: "en-CA", href: data.hreflang_en }));
      meta.push(setMeta('link[rel="alternate"][hreflang="x-default"]', { rel: "alternate", hreflang: "x-default", href: data.hreflang_en }));
    }

    // 1) FAQPage — picks up rich FAQ snippets + AI Overview answers.
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": (data.faqs || []).map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    };
    // 2) HowTo — Google's AI Overview pulls steps from HowTo schema.
    const howToLd = data.intent === "how" ? {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": data.question,
      "description": data.summary,
      "totalTime": "PT15M",
      "supply": [],
      "tool": [{ "@type": "HowToTool", "name": "Central Rentals (paid plans from CA$35/month)" }],
      "step": (data.steps || []).map((s, i) => ({
        "@type": "HowToStep",
        "position": i + 1,
        "name": `Step ${i + 1}`,
        "text": s,
        "url": `${data.canonical}#step-${i + 1}`,
      })),
    } : null;
    // 3) Speakable — voice / AI assistant eligibility. We mark the summary
    //    paragraph and step list as speakable.
    const speakableLd = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": data.canonical,
      "name": data.question,
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".answer-summary", ".answer-steps"],
      },
    };
    // 4) LocalBusiness — province-scoped service area so we appear in
    //    "[province] property management software" local AI queries.
    const localBizLd = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Central Rentals — Property Management Software (${data.province.name})`,
      "url": data.canonical,
      "image": "https://centralrentals.ca/brand-logo.png",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "CA",
      },
      "areaServed": {
        "@type": "AdministrativeArea",
        "name": data.province.name,
        "addressCountry": "CA",
      },
      "telephone": "+1-780-405-2498",
      "priceRange": "CA$35 / month",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "127" },
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Answers", "item": "https://centralrentals.ca/answers" },
        { "@type": "ListItem", "position": 2, "name": data.province.name, "item": `https://centralrentals.ca/answers/${data.province_slug}` },
        { "@type": "ListItem", "position": 3, "name": data.question, "item": data.canonical },
      ],
    };
    const scripts = [
      setJsonLd("faq", faqLd),
      setJsonLd("speakable", speakableLd),
      setJsonLd("localbiz", localBizLd),
      setJsonLd("breadcrumb", breadcrumbLd),
      ...(howToLd ? [setJsonLd("howto", howToLd)] : []),
    ];
    return () => {
      document.title = prevTitle;
      document.documentElement.setAttribute("lang", prevHtmlLang || "en");
      meta.forEach((m) => m?.remove?.());
      scripts.forEach((s) => s?.remove?.());
    };
  }, [data]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500"><PublicHeader/>Answer not found.</div>;
  if (!data) return <div className="min-h-screen bg-white"><PublicHeader/><div className="max-w-4xl mx-auto px-5 py-16 text-slate-400">Loading…</div></div>;

  const T = STR[data.lang === "fr" ? "fr" : "en"];
  const answersUrl = data.lang === "fr" ? "/fr/answers" : "/answers";

  return (
    <div className="min-h-screen bg-white" data-testid={`answer-${data.province_slug}-${data.topic}`}>
      <PublicHeader/>

      {/* Breadcrumb */}
      <nav className="max-w-4xl mx-auto px-5 pt-6 text-xs text-slate-500 flex items-center gap-1.5" aria-label="Breadcrumb">
        <Link to={answersUrl} className="hover:text-emerald-700">{T.breadcrumb_answers}</Link>
        <ChevronRight className="w-3 h-3"/>
        <Link to={`${answersUrl}/${data.province_slug}/${data.topic}`} className="hover:text-emerald-700">{data.province.name}</Link>
        <ChevronRight className="w-3 h-3"/>
        <span className="text-slate-700 font-semibold truncate">{data.question}</span>
      </nav>

      {/* H1 + summary (speakable) */}
      <header className="max-w-4xl mx-auto px-5 pt-6 pb-8">
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
          <MapPin className="w-3.5 h-3.5"/> {data.province.name} · Updated for 2026
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.1] tracking-tight text-slate-900" data-testid="answer-h1">
          {data.question}
        </h1>
        <p className="answer-summary mt-6 text-lg text-slate-700 leading-relaxed max-w-3xl" data-testid="answer-summary">
          {data.summary}
        </p>
      </header>

      {/* Steps / Why */}
      <main className="max-w-4xl mx-auto px-5 pb-14">
        <section className="answer-steps" data-testid="answer-steps" aria-labelledby="steps-heading">
          <h2 id="steps-heading" className="font-display text-2xl font-extrabold text-slate-900 mb-5 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600"/> {T.step_by_step} {data.province.name}
          </h2>
          <ol className="space-y-3">
            {(data.steps || []).map((s, i) => (
              <li key={i} id={`step-${i + 1}`} className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
                <div className="flex-none w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                <div className="text-[15px] text-slate-800 leading-relaxed">{s}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12" aria-labelledby="why-heading">
          <h2 id="why-heading" className="font-display text-2xl font-extrabold text-slate-900 mb-3">{T.why_this_matters}</h2>
          <div className="rounded-2xl bg-gradient-to-br from-amber-50 via-white to-rose-50 border border-amber-200 p-6">
            <p className="text-slate-800 leading-relaxed text-[15px]">{data.why}</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12" aria-labelledby="faq-heading" data-testid="answer-faqs">
          <h2 id="faq-heading" className="font-display text-2xl font-extrabold text-slate-900 mb-5 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-violet-600"/> {T.faq}
          </h2>
          <div className="space-y-3">
            {(data.faqs || []).map((f, i) => (
              <details key={i} className="rounded-xl border border-slate-200 p-5 group">
                <summary className="font-bold text-slate-900 cursor-pointer text-[15px] group-open:mb-3">{f.q}</summary>
                <p className="text-slate-700 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Cluster CTA */}
        <section className="mt-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-7 sm:p-9" aria-labelledby="cta-heading">
          <h2 id="cta-heading" className="font-display text-2xl font-extrabold mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5"/> {T.cta_heading}</h2>
          <p className="text-emerald-50 text-[15px] mb-5">{T.cta_body(data.province)}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-emerald-700 font-bold text-[15px] shadow-lg hover:scale-[1.02] transition-transform" data-testid="answer-cta-signup">
              {T.cta_btn} <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to={`/landlord-software/${data.province_slug}`} className="text-sm font-bold text-emerald-50 hover:text-white underline-offset-2 hover:underline">
              {T.cta_secondary(data.province)}
            </Link>
          </div>
        </section>

        {/* Related answers in this province */}
        <RelatedAnswers lang={data.lang || "en"} province={data.province_slug} excludeTopic={data.topic} provinceName={data.province.name}/>
      </main>
    </div>
  );
}

/* ─────────────────────────────── Related ─────────────────────────────── */

function RelatedAnswers({ province, excludeTopic, provinceName, lang = "en" }) {
  const [items, setItems] = useState([]);
  const T = STR[lang === "fr" ? "fr" : "en"];
  const linkPrefix = lang === "fr" ? "/fr/answers" : "/answers";
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/aeo/answers?province=${province}`);
        const j = await r.json();
        setItems((j.items || []).filter((i) => i.topic !== excludeTopic));
      } catch { /* noop */ }
    })();
  }, [province, excludeTopic]);
  if (!items.length) return null;
  return (
    <section className="mt-14 border-t border-slate-200 pt-10" data-testid="answer-related">
      <h2 className="font-display text-xl font-extrabold text-slate-900 mb-5">{T.more_answers({ name: provinceName })}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <Link key={it.url} to={`${linkPrefix}/${it.province_slug}/${it.topic}`} className="block rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all p-4 group">
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">{it.intent === "how" ? T.how_to : T.what_is}</div>
            <div className="mt-1 font-bold text-slate-900 group-hover:text-emerald-700 text-[15px] leading-snug">{it.question}</div>
            <div className="mt-2 inline-flex items-center gap-1 text-emerald-700 text-xs font-bold">
              {T.read_answer} <ArrowRight className="w-3.5 h-3.5"/>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────── Hub ─────────────────────────────── */

export function AnswerHub() {
  const [items, setItems] = useState([]);
  const [province, setProvince] = useState("all");
  const provinces = useMemo(() => {
    const seen = new Set();
    return items.filter((i) => { if (seen.has(i.province_slug)) return false; seen.add(i.province_slug); return true; });
  }, [items]);

  useEffect(() => {
    document.title = "Provincial answer hub — Central Rentals Canada";
    setMeta('meta[name="description"]', { name: "description", content: "Province-by-province answers for Canadian landlords and tenants. Rent increase rules, eviction process, deposit caps, T776 deductions, screening, and more — for all 13 provinces and territories." });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/answers" });
    (async () => {
      try {
        const r = await fetch(`${API}/api/aeo/answers`);
        const j = await r.json();
        setItems(j.items || []);
      } catch { /* noop */ }
    })();
  }, []);

  const filtered = province === "all" ? items : items.filter((i) => i.province_slug === province);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader/>
      <section className="max-w-6xl mx-auto px-5 pt-10 pb-6">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900">Canadian landlord + tenant answer hub</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-3xl">Province-by-province, step-by-step answers to the questions Canadian landlords and tenants ask Google + search assistants most. Updated for 2026. {items.length} answers across all 13 provinces and territories.</p>
      </section>

      <div className="max-w-6xl mx-auto px-5 pb-2 flex items-center gap-2 flex-wrap" data-testid="answer-hub-filters">
        <button onClick={() => setProvince("all")} className={`px-3 py-1.5 rounded-full text-xs font-bold ${province === "all" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>All</button>
        {provinces.map((p) => (
          <button key={p.province_slug} onClick={() => setProvince(p.province_slug)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${province === p.province_slug ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`answer-hub-filter-${p.province_code}`}>
            {p.province_name}
          </button>
        ))}
      </div>

      <section className="max-w-6xl mx-auto px-5 py-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="answer-hub-grid">
        {filtered.map((it) => (
          <Link key={it.url} to={it.url} className="block rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all p-5 group">
            <div className="flex items-center justify-between text-[10px] tracking-widest uppercase">
              <span className="text-violet-700 font-bold">{it.intent === "how" ? "How to" : "What is"}</span>
              <span className="text-slate-400">{it.province_name}</span>
            </div>
            <div className="mt-2 font-display text-lg font-bold text-slate-900 group-hover:text-emerald-700 leading-snug">{it.question}</div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 text-xs font-bold">
              <CheckCircle2 className="w-3.5 h-3.5"/> Step-by-step answer
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}
